/**
 * Estela visual sutil para las bolas del plinko: cada cierto intervalo deja
 * una copia semitransparente que se desvanece rápido, dando sensación de
 * velocidad en la cascada sin sumar cuerpos físicos nuevos — las copias son
 * solo `Image` decorativas, nunca cuerpos Matter, así que no afectan el
 * límite de rendimiento de `MAX_VISUAL_BALLS`.
 *
 * EXCEPCIÓN DELIBERADA a la regla "sin Phaser en plinko/logic": este archivo
 * es puramente decorativo/render, así que sí importa Phaser (igual que
 * `BallPool`, `Board`, `PourController`).
 */

import Phaser from 'phaser';
import { TEX } from '../core/TextureFactory';

/** Intervalo mínimo (ms) entre dos puntos de estela de una misma bola. */
const TRAIL_INTERVAL_MS = 45;
/** Duración (ms) del desvanecido de cada punto de estela. */
const TRAIL_LIFE_MS = 180;
/** Tope de puntos de estela vivos a la vez, para no sumar costo en avalanchas grandes. */
const MAX_TRAIL_DOTS = 40;

export class BallTrail {
  private readonly lastSpawnMs = new Map<number, number>();
  private activeDots = 0;

  constructor(private readonly scene: Phaser.Scene) {}

  /** Llamar cada frame por cada bola activa, con su posición actual y el reloj de la escena. */
  update(bodyId: number, x: number, y: number, nowMs: number): void {
    if (this.activeDots >= MAX_TRAIL_DOTS) return;
    const last = this.lastSpawnMs.get(bodyId) ?? 0;
    if (nowMs - last < TRAIL_INTERVAL_MS) return;
    this.lastSpawnMs.set(bodyId, nowMs);
    this.spawnDot(x, y);
  }

  /** Olvida el temporizador de una bola que ya se asentó/recicló (evita fugas del mapa). */
  forget(bodyId: number): void {
    this.lastSpawnMs.delete(bodyId);
  }

  private spawnDot(x: number, y: number): void {
    const dot = this.scene.add.image(x, y, TEX.ball).setScale(0.55).setAlpha(0.3);
    this.activeDots++;
    this.scene.tweens.add({
      targets: dot,
      alpha: 0,
      scale: 0.15,
      duration: TRAIL_LIFE_MS,
      ease: 'Sine.Out',
      onComplete: () => {
        dot.destroy();
        this.activeDots--;
      },
    });
  }

  destroy(): void {
    this.lastSpawnMs.clear();
  }
}
